import lodash from 'lodash'
import Vue from 'vue'

import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'
import i18n from '@/utils/i18n'
import notification from '@/utils/notification'
import store from '@/store'
import types from '@/store/mutation-types'
import * as insuranceAPI from '@/api/insurance/request'
import * as inspectionAPI from '@/api/inspection/request'
import * as projectAPI from '@/api/projects/request'
import * as tenanciesApi from '@/api/tenancies/request'
import * as tenantProjectApi from '@/api/tenantProjects/request'
import * as workPermitApi from '@/api/workPermit/request'
import { ActionTypes } from '@/utils/consts'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { checkIfHKLProjectRelatedRoute } from '@/router/utils'
import { DOCS_SORT_RULES } from '@/api/docs/consts'
import { GUIDELINE_STATUS } from '@/utils/locale'
import { groupWorkPermits, getBuilding, navigate } from '@/api/workPermit/util'
import { getAttachments } from '@/api/docs/request'
import { getGuidelines } from '@/api/settings/request'
import { getFloorIndex } from '@/api/buildings/request'
import { WORK_PERMIT_TYPES, WORK_PERMIT_STATUS } from '@/modules/workPermits/consts'

const defaultState = getDefaultState()

function getDefaultState () {
  return {
    workPermitsInTenancy: [],
    workPermitsInProject: [],
    parentPermit: {},
    currentWorkPermit: {},
    currentReviewWorkPermit: {context: null, projectId: null},
    reviewMap: new Map(),
    reviewQueue: [],
    isUnderBatch: {context: false},
    lastSearchedAttributes: {},
    allWorkPermits: [],
    buildingFloorZoneMap: [],
    quickSearchEnabled: false,
  }
}

// gettersworkPermitsInProject
const getters = {
  workPermitsInTenancy: state => state.workPermitsInTenancy || [],
  workPermitsInProject: state => state.workPermitsInProject || [],
  reviewMap: state => state.reviewMap || new Map(),
  reviewQueue: state => state.reviewQueue || [],
  currentReviewWorkPermit: state => state.currentReviewWorkPermit || {},
  currentWorkPermit: state => state.currentWorkPermit,
  isStandalone: state => state.currentWorkPermit && state.currentWorkPermit.standalone,
  parentPermit: state => state.parentPermit || {},
  lastSearchedAttributes: state => state.lastSearchedAttributes,
  allWorkPermits: state => state.allWorkPermits,
  buildingFloorZoneMap: state => state.buildingFloorZoneMap || [],
  isUnderBatch: state => state.isUnderBatch && Object.values(state.isUnderBatch).some(val => val),
  quickSearchEnabled: state => state.quickSearchEnabled,
  isEntitledTenancyAccount: (state, getters, rootState, rootGetters) => getIsEntitledTenancyAccount(getters, rootGetters),
  isTenantHasStandaloneAccess: (state, getters, rootState, rootGetters) => getIsTenantHasStandaloneAccess(getters, rootGetters),
}

// mutations
const mutations = {
  [types.WORKPERMIT_SET_QUICKSEARCH] (state, value) {
    state.quickSearchEnabled = value
  },
  [types.WORKPERMIT_SET_PROJECT_WORKPERMIT] (state, workPermits = []) {
    state.workPermitsInProject = workPermits
  },
  [types.WORKPERMIT_SET_TENANCY_WORKPERMIT] (state, workPermits = []) {
    state.workPermitsInTenancy = workPermits
  },
  [types.WORKPERMIT_SET_CURRENT_WORKPERMIT] (state, workPermit) {
    state.currentWorkPermit = lodash.cloneDeep(workPermit)
    if (lodash.isEmpty(workPermit.workers)) {
      state.currentWorkPermit.workers = [{
        firstName: '',
        lastName: '',
        cardNum: '',
        remarks: '',
        sourceId: workPermit.id,
        projectId: workPermit.projectId,
      }]
    }
  },
  [types.WORKPERMIT_SET_WORKPERMIT_FIELD_BY_NAME] (state, {name, value}) {
    Vue.set(state['currentWorkPermit'], name, value)
  },
  [types.RESET_STATE] (state) {
    const stateVal = getDefaultState()
    for (const field in stateVal) {
      Vue.set(state, field, stateVal[field])
    }
  },
  [types.WORKPERMIT_SET_SEARCH_ATTRS] (state, filters = {}) {
    state.lastSearchedAttributes = filters
  },
  [types.WORKPERMIT_SET_SEARCH_RESULT] (state, result) {
    state.allWorkPermits = result
  },
  [types.WORKPERMIT_ADD_NEW_HOISTING] (state, { id, isTenantProject = state.currentWorkPermit.isTenantProject, contractorId }) {
    const hoistings = lodash.cloneDeep(lodash.get(state.currentWorkPermit, 'hoistings', []))
    const hoisting = {
      contractorId,
      description: '',
      endDate: null,
      floors: [],
      id,
      type: WORK_PERMIT_TYPES.HOISTING,
      projectId: state.currentWorkPermit.projectId,
      status: WORK_PERMIT_STATUS.DRAFT,
      startDate: null,
      parentId: state.currentWorkPermit.id,
      workArea: '',
      hotWork: {},
      isTenantProject,
      standalone: state.currentWorkPermit.standalone,
      sourceId: state.currentWorkPermit.sourceId,
    }
    if (!isTenantProject) {
      hoisting.buildingId = state.currentWorkPermit.buildingId
      hoisting.location = state.currentWorkPermit.location
    }
    hoistings.push(hoisting)
    Vue.set(state.currentWorkPermit, 'hoistings', hoistings)
  },
  [types.WORKPERMIT_SET_HOISTING_ATTRIBUTE] (state, {id, name, value}) {
    state.currentWorkPermit.hoistings.map((item, index) => {
      if (item.id === id) {
        Vue.set(state.currentWorkPermit.hoistings[index], name, value)
      }
    })
  },
  [types.WORKPERMIT_REPLACE_HOISTING] (state, newHoisting) {
    const index = lodash.findIndex(state.currentWorkPermit.hoistings, hoisting => {
      return hoisting.id === newHoisting.id
    })
    if (index !== -1) {
      state.currentWorkPermit.hoistings.splice(index, 1, newHoisting)
    }
  },
  [types.WORKPERMIT_SET_WORKER_INFO] (state, workerInfo) {
    Vue.set(state.currentWorkPermit, 'workers', workerInfo)
  },
  [types.WORKPERMIT_SET_VALUE_FOR_WORKER_INFO] (state, {index, name, value}) {
    Vue.set(state.currentWorkPermit.workers[index], name, value)
  },
  [types.WORKPERMIT_SET_HOT_WORK_INFO] (state, {hoistingId, name, value}) {
    state.currentWorkPermit.hoistings.map((item, index) => {
      if (item.id === hoistingId) {
        Vue.set(state.currentWorkPermit.hoistings[index]['hotWork'], name, value)
      }
    })
  },
  [types.WORKPERMIT_ADD_HOT_WORK] (state, hoistingId) {
    const hotWork = {
      parentId: hoistingId,
      type: '',
      description: '',
      equipment: '',
      precautions: '',
    }
    state.currentWorkPermit.hoistings.map((item, index) => {
      if (item.id === hoistingId) {
        Vue.set(state.currentWorkPermit.hoistings[index], 'hotWork', hotWork)
      }
    })
  },
  [types.WORKPERMIT_DELETE_HOT_WORK] (state, hoistingId) {
    state.currentWorkPermit.hoistings.map((item, index) => {
      if (item.id === hoistingId) {
        Vue.set(state.currentWorkPermit.hoistings[index], 'hotWork', null)
      }
    })
  },
  [types.WORKPERMIT_DELETE_HOISTING_LOCAL] (state, hoistingId) {
    const index = lodash.findIndex(state.currentWorkPermit.hoistings, hoisting => {
      return hoistingId === hoisting.id
    })
    state.currentWorkPermit.hoistings.splice(index, 1)
    if (state.workPermitsInProject.length) {
      const workPermitIndex = lodash.findIndex(state.workPermitsInProject, permit => {
        return permit.self.id === state.currentWorkPermit.id
      })
      const hoistingIndex = lodash.findIndex(state.workPermitsInProject[workPermitIndex].hoistings, hoisting => {
        return hoistingId === hoisting.id
      })
      state.workPermitsInProject[workPermitIndex].hoistings.splice(hoistingIndex, 1)
    }
  },
  [types.WORKPERMIT_SET_PARENT_PERMIT] (state, permit) {
    state.parentPermit = permit
  },
  [types.WORKPERMIT_SET_BUILDING_FLOOR_MAP] (state, buildingFloorZoneMap) {
    state.buildingFloorZoneMap = buildingFloorZoneMap
  },
  [types.WORKPERMIT_SET_REVIEW_QUEUE] (state, payload) {
    state.reviewQueue = payload
  },
  [types.WORKPERMIT_SET_REVIEW_MAP] (state, payload) {
    state.reviewMap = payload
  },
  [types.WORKPERMIT_DEQUEUE_REVIEW_QUEUE] (state) {
    if (state.reviewQueue && state.reviewQueue.length > 0) {
      let workpermit = state.reviewQueue.shift()
      state.currentReviewWorkPermit = workpermit
    } else {
      state.currentReviewWorkPermit = defaultState.currentReviewWorkPermit
    }
  },
  [types.WORKPERMIT_SET_UNDER_BATCH] (state, payload) {
    Vue.set(state.isUnderBatch, 'context', payload)
  },
  [types.WORKPERMIT_ADD_TO_REVIEW_QUEUE_HEAD] (state, hoistings = []) {
    state.reviewQueue.unshift(...hoistings)
  },
}

const actions = {
  async createWorkPermit ({commit}, payload) {
    const workPermit = await workPermitApi.createWorkPermit(payload)
    if (payload.type !== WORK_PERMIT_TYPES.HOISTING) {
      commit(types.WORKPERMIT_SET_CURRENT_WORKPERMIT, workPermit)
    }
    return workPermit
  },
  async getAllWorkPermits ({ commit }, { filters, keep = false, isSilentGet = false, quickSearch = false }) {
    let list = await workPermitApi.getAllWorkPermits(filters, isSilentGet) || []
    if (quickSearch) {
      list = lodash.orderBy(list,
        workPermit => workPermit.contractor.name,
        workPermit => getBuilding(workPermit),
        workPermit => getFloorIndex(getBuilding(workPermit), workPermit.floors))
    }
    if (keep) {
      commit(types.WORKPERMIT_SET_SEARCH_RESULT, list)
    }
    return list
  },
  async getAllWPListByPagination ({ commit }, { filters }) {
    let list = await workPermitApi.getWpList(filters) || []
    commit(types.WORKPERMIT_SET_SEARCH_RESULT, list)
    return list
  },
  async getAllWorkPermitsByTenantId ({commit}, sourceId) {
    let list = await workPermitApi.getAllWorkPermits(sourceId) || []
    const groupedItems = groupWorkPermits(list)
    commit(types.WORKPERMIT_SET_TENANCY_WORKPERMIT, groupedItems)
    return groupedItems
  },
  setWorkPermitsForTenantProject ({ commit }, { permits = [], needRoot = false }) {
    const groupedItems = groupWorkPermits(permits)
    const type = needRoot ? `workPermit/${types.WORKPERMIT_SET_PROJECT_WORKPERMIT}` : types.WORKPERMIT_SET_PROJECT_WORKPERMIT
    commit(type, groupedItems, needRoot ? { root: true } : '')
  },
  async getWorkPermitsInProjectByProjectId ({ getters: {
    parentPermit,
    currentWorkPermit,
  }, commit, rootGetters: {
    'user/isEngineer': isEngineer,
    'projects/currentProject': currentProject,
  } }, { projectId, changeCurrentWorkPermitId }) {
    // Inject buildingIncluded info for https://jirapct.epam.com/jira/browse/HKLDCMS-23965
    // This depends on currentProject, please make sure currentProject was loaded before
    const hkldEngineerAccessHKLProject = currentProject.id === projectId && isEngineer
    const buildingIncludedQuery = hkldEngineerAccessHKLProject ? {
      buildingIncludedRequired: currentProject.buildingIncludedRequired,
      buildingIncluded: currentProject.buildingIncluded,
    } : {}
    const isTenantProject = checkIfHKLProjectRelatedRoute() ? 0 : 1
    const queryParam = {
      limitAttrs: true,
      ...buildingIncludedQuery,
      excludeModels: {'projects': true},
      isTenantProject,
    }
    const list = await workPermitApi.getAllWorkPermitsInProject(projectId, queryParam)
    const groupedItems = groupWorkPermits(list)

    const currentWorkPermitItem = groupedItems.find(item => item.self.id === changeCurrentWorkPermitId)
    if (changeCurrentWorkPermitId && currentWorkPermitItem) {
      currentWorkPermitItem.self.hoistings = currentWorkPermitItem.hoistings
      commit(types.WORKPERMIT_SET_CURRENT_WORKPERMIT, currentWorkPermitItem.self)
    }
    const unavailableStatus = [WORK_PERMIT_STATUS.CANCELLED, WORK_PERMIT_STATUS.REJECTED]
    const parentWorkPermitItem = groupedItems.find(
      item => item.self.type === WORK_PERMIT_TYPES.PROJECT && !unavailableStatus.includes(item.self.status),
    )
    const currentParentWPIsProjectWP = !lodash.isEmpty(parentPermit) &&
      !lodash.isEmpty(currentWorkPermit) &&
      currentWorkPermit.parentId === parentPermit.id &&
      parentPermit.type === WORK_PERMIT_TYPES.PROJECT
    // getAllWorkPermits does not return project work permit because of user permission limit
    // and parent permit would be wrongly reset when it's project permit
    // Then, add noNeedToResetParentWP check
    const noNeedToResetParentWP = currentParentWPIsProjectWP && lodash.isEmpty(parentWorkPermitItem)
    if (!noNeedToResetParentWP) {
      commit(types.WORKPERMIT_SET_PARENT_PERMIT, parentWorkPermitItem ? parentWorkPermitItem.self : {})
    }

    commit(types.WORKPERMIT_SET_PROJECT_WORKPERMIT, groupedItems)
    return groupedItems
  },
  async getWorkPermitById ({dispatch}, id) {
    const workPermit = await workPermitApi.getWorkPermitById(id)
    await dispatch('setWorkPermit', workPermit)
    return workPermit
  },
  async getWPByIdSkipCache ({dispatch}, id) {
    const workPermit = await workPermitApi.getWorkPermitById(id, {noCache: true})
    await dispatch('setWorkPermit', workPermit)
    return workPermit
  },
  async setWorkPermit ({commit}, workPermit) {
    commit(types.WORKPERMIT_SET_CURRENT_WORKPERMIT, workPermit)
    commit(types.WORKPERMIT_SET_PARENT_PERMIT, {})
    if (workPermit.parentId) {
      const parentPermits = await workPermitApi.getTPProjectPermit(workPermit.projectId)
      parentPermits.forEach(item => {
        if (item.type === WORK_PERMIT_TYPES.PROJECT) {
          commit(types.WORKPERMIT_SET_PARENT_PERMIT, item)
        }
      })
    }
  },
  async updateWorkPermitById ({commit, dispatch, state}) {
    const request = state.currentWorkPermit
    const workPermit = await workPermitApi.updateWorkPermitById(request.id, request)
    if (workPermit) {
      await dispatch('getWorkPermitsInProjectByProjectId', { projectId: workPermit.projectId })
      await dispatch('getWorkPermitById', request.id)
    }
    return workPermit
  },

  async updateHoistingById ({dispatch}, {request}) {
    await workPermitApi.updateHoistingById(request.id, request)
    const hoisting = await workPermitApi.getWorkPermitById(request.id, {excludeModels: {workers: true, hoistings: true, building: true}})

    hoisting.projectId
      ? await dispatch('getWorkPermitsInProjectByProjectId', { projectId: hoisting.projectId, changeCurrentWorkPermitId: hoisting.parentId })
      : await dispatch('getWorkPermitById', hoisting.parentId)

    return hoisting
  },

  async updateWorkPermitByExtendDate ({commit, dispatch, state}, {request, extendType, id = state.currentWorkPermit.id}) {
    let extendWP
    if (extendType) {
      switch (extendType) {
        case 'confirm': extendWP = await workPermitApi.extendWorkPermit(id, request)
          break
        case 'HKLandExtend': extendWP = await workPermitApi.extendWorkPermit(id, request)
          break
        case 'request': extendWP = await workPermitApi.requestExtendWorkPermit(id, request)
          break
      }
    }
    if (extendWP) {
      commit(types.WORKPERMIT_SET_CURRENT_WORKPERMIT, extendWP)
      await dispatch('getWorkPermitsInProjectByProjectId', { projectId: extendWP.projectId })
    }
    return extendWP
  },
  async submitWorkPermit ({commit, dispatch, state}, request = state.currentWorkPermit) {
    const newWP = await workPermitApi.updateWorkPermitById(request.id, request, ActionTypes.workPermits.submit)
    dispatch('getWorkPermitById', request.id)
    return newWP
  },
  async submitHoisting ({commit, dispatch, state}, hoisting) {
    commit(types.WORKPERMIT_SET_HOISTING_ATTRIBUTE, {name: 'status', id: hoisting.id, value: WORK_PERMIT_STATUS.WAPPR})
    const newHoisting = await workPermitApi.updateWorkPermitById(hoisting.id, hoisting, ActionTypes.workPermits.submit)

    hoisting.projectId
      ? await dispatch('getWorkPermitsInProjectByProjectId', { projectId: newHoisting.projectId, changeCurrentWorkPermitId: newHoisting.parentId })
      : await dispatch('getWorkPermitById', newHoisting.parentId)

    return newHoisting
  },

  async changeWorkPermitStatus ({commit, dispatch, state}, {
    status,
    historyDetail,
    id = state.currentWorkPermit.id,
    updatedAt = state.currentWorkPermit.updatedAt,
    suspendEffectiveDate,
    projectId = state.currentWorkPermit.projectId,
    isResumeAction = false,
    nextReviewerId,
    refresh = true,
    files,
  }) {
    const result = await workPermitApi.changeWorkPermitStatus(id, {suspendEffectiveDate, status, updatedAt, historyDetail, nextReviewerId, isResumeAction, files})
    if (refresh) {
      // TODO @Zoe add await and remove duplicate request
      dispatch('getWorkPermitById', id)
      dispatch('getWorkPermitsInProjectByProjectId', { projectId })
    }
    return result
  },
  async updateWorkerInfoBySourceId ({commit}, {sourceId, request}) {
    const workers = await workPermitApi.updateWorkerInfoBySourceId(sourceId, request)
    commit(types.WORKPERMIT_SET_WORKER_INFO, workers)
  },
  async updateWorkPermitManager ({dispatch, state}, {request}) {
    const id = state.currentWorkPermit.id
    const projectId = state.currentWorkPermit.projectId
    const result = await workPermitApi.updateWorkPermitManager(id, request)
    await dispatch('getWPByIdSkipCache', state.currentWorkPermit.id)
    if (projectId) {
      await dispatch('getWorkPermitsInProjectByProjectId', { projectId })
    }
    return result
  },
  async deleteWorkPermit ({dispatch}, workPermit) {
    await workPermitApi.deleteWorkPermit(workPermit.id)
    if (workPermit.type !== WORK_PERMIT_TYPES.PROJECT) {
      const projectId = workPermit.parentId || workPermit.projectId
      dispatch('getWorkPermitsInProjectByProjectId', { projectId })
    }
  },
  async deleteHoistingLocalAndServer ({dispatch, commit}, hoisting) {
    commit(types.WORKPERMIT_DELETE_HOISTING_LOCAL, hoisting.id)
    await workPermitApi.deleteWorkPermit(hoisting.id)
  },
  setReviewQueue (context, rawList) {
    let curId
    let projectWpMap = new Map()
    let queue = []
    // map
    for (let i = 0, len = rawList.length; i < len; i++) {
      curId = rawList[i].projectId
      if (projectWpMap.has(curId)) {
        projectWpMap.set(curId, [...projectWpMap.get(curId), rawList[i]])
      } else {
        projectWpMap.set(curId, [rawList[i]])
      }
    }
    // reduce
    for (let [key, val] of projectWpMap) {
      let projectWp = []
      val.forEach(element => {
        // enqueue
        let item = {
          context: element,
          projectId: key,
        }
        if (element.type === WORK_PERMIT_TYPES.PROJECT) {
          projectWp.unshift(item)
        } else {
          projectWp.push(item)
        }
      })
      queue.push(...projectWp)
    }
    context.commit(types.WORKPERMIT_SET_REVIEW_MAP, projectWpMap)
    context.commit(types.WORKPERMIT_SET_REVIEW_QUEUE, queue)
  },
  scheduleXHRForNextWorkPermit ({state}) {
    if (!state.reviewQueue[0] || !state.currentReviewWorkPermit || !state.reviewQueue[0].projectId) return
    const current = state.currentReviewWorkPermit
    const wp = state.reviewQueue[0].context
    const projectId = state.reviewQueue[0].projectId
    const isTenantProject = wp.isTenantProject
    const isSameProject = current.projectId === projectId
    const projectRelatedSchedule = !isSameProject ? (isTenantProject ? scheduleXHRForTenantProject : scheduleXHRForProject) : function () {}
    setTimeout(() => {
      commonSchedule(wp)
      projectRelatedSchedule(projectId)
    }, 1500)
  },
  async dequeueReviewQueue ({commit, state, dispatch}) {
    const cur = state.currentReviewWorkPermit && state.currentReviewWorkPermit.context
    if (cur) {
      // if has hoisting
      const hoistings = cur.hoistings
      if (hoistings && hoistings.length > 0) {
        // check if the work permit is at APPR status
        let wapprHoistings = await getWAPPRHoistings(cur.id, hoistings)
        commit(types.WORKPERMIT_ADD_TO_REVIEW_QUEUE_HEAD, wapprHoistings)
      }
    }
    commit(types.WORKPERMIT_DEQUEUE_REVIEW_QUEUE)
    dispatch('scheduleXHRForNextWorkPermit')
  },
  async advanceReviewQueueAndNavigate ({ commit, dispatch, getters }) {
    await dispatch('dequeueReviewQueue')
    const permit = getters.currentReviewWorkPermit && getters.currentReviewWorkPermit.context
    if (!permit) {
      notification.fireNotification(true, i18n.t('workPermit.lastApprove'))
      EventBus.emit('wp-review-ended')
      commit(types.WORKPERMIT_SET_UNDER_BATCH, false)
      return null
    }
    const hoistingId = permit.type === WORK_PERMIT_TYPES.HOISTING ? permit.id : undefined
    navigate(permit, hoistingId, { fromViewAll: false, underBatch: true })
    return permit
  },
  async getOwnTenancies (isSilentGet) {
    const res = tenanciesApi.getOwnTenancies(isSilentGet)
    return res
  },
}

function commonSchedule (wp) {
  getGuidelines(GUIDELINE_STATUS.PUBLISHED, true)
  if (wp.isTenantProject || wp.type === WORK_PERMIT_TYPES.HOISTING) {
    getAttachments(wp.id, undefined, undefined, true)
  }
}

function scheduleXHRForTenantProject (projectId) {
  const isTenant = store.getters['user/isTenant']
  const shouldLoadDeposit = isTenant || aclHelper.getAccess(tmsPermsTypes.project.readDeposit)
  const shouldLoadInsurance = isTenant || aclHelper.getAccess(tmsPermsTypes.project.readInsurance)
  const shouldLoadInspection = isTenant || aclHelper.getAccess(tmsPermsTypes.project.readInspection)
  const shouldLoadSubmissionDocs = !isTenant || store.getters['tenantProject/canTenantRead']

  tenantProjectApi.loadTenantProject(projectId, true).then((project) => {
    if (shouldLoadSubmissionDocs && project && project.projectDocsSrcId) {
      getAttachments(project.projectDocsSrcId, undefined, DOCS_SORT_RULES.TENANT_PROJECT_DOCS, true)
      getAttachments(project.projectDocsSrcId, undefined, undefined, true)
    }
    if (shouldLoadDeposit && project && project.deposit.sourceId) {
      getAttachments(project.deposit.sourceId, undefined, undefined, true)
    }
    if (project && project.finalDocsSrcId) {
      getAttachments(project.finalDocsSrcId, undefined, undefined, true)
    }
  })

  if (shouldLoadInsurance) {
    insuranceAPI.get({projectId}, true).then((insurance) => {
      if (insurance[0] && insurance[0].id) {
        getAttachments(insurance[0].id, undefined, undefined, true)
      }
    })
  }

  if (shouldLoadInspection) {
    inspectionAPI.getALL({projectId}, true)
  }

  workPermitApi.getTPProjectPermit(projectId)
}

function scheduleXHRForProject (projectId) {
  const isContractor = store.getters['user/isContractor']
  const shouldLoadProjectDoc = aclHelper.hasProjectsAccess(isContractor)

  projectAPI.getProject(projectId, true).then(project => {
    if (shouldLoadProjectDoc && project) {
      if (project.deductionSrcId) {
        getAttachments(project.deductionSrcId, undefined, undefined, true)
      }
      if (project.poAttachmentId) {
        getAttachments(project.poAttachmentId, undefined, undefined, true)
      }
    }
  })

  if (shouldLoadProjectDoc) {
    getAttachments(projectId, undefined, undefined, true)
  }
}

async function getWAPPRHoistings (wpId, hoistings) {
  const workPermit = await workPermitApi.getWorkPermitById(wpId)
  let wapprHoistings = []
  if (workPermit.status === WORK_PERMIT_STATUS.APPR) {
    const canApproveWithinOfficeHoursHP = aclHelper.getAccess(tmsPermsTypes.workpermit.approveOfficeHP)
    const canApproveAfterOfficeHoursHP = aclHelper.getAccess(tmsPermsTypes.workpermit.approveNonOfficeHP)
    for (let i = 0; i < hoistings.length; i++) {
      if (hoistings[i].status !== WORK_PERMIT_STATUS.WAPPR) {
        continue
      }
      const isWithinOfficeHours = dateUtil.isWithinOfficeHours(hoistings[i].startDate, hoistings[i].endDate)
      if ((isWithinOfficeHours && canApproveWithinOfficeHoursHP) || (!isWithinOfficeHours && canApproveAfterOfficeHoursHP)) {
        wapprHoistings.push({
          context: hoistings[i],
          projectId: hoistings[i].projectId,
        })
      }
    }
  }
  return wapprHoistings
}

function getIsEntitledTenancyAccount (getters, rootGetters) {
  if (!getters.currentWorkPermit.standalone || !getters.currentWorkPermit.isTenantProject) return false

  const currentUser = rootGetters['user/currentUser'] || {}
  const accounts = lodash.get(getters.currentWorkPermit, 'tenancy.accounts', [])
  return accounts.some(item =>
    item.id === currentUser.id && lodash.get(item, 'TenancyAccount.perms.StandaloneWorkPermits', false))
}

function getIsTenantHasStandaloneAccess (getters, rootGetters) {
  if (!getters.currentWorkPermit.standalone || !getters.currentWorkPermit.isTenantProject) return false

  const tenancyContractors = lodash.get(getters.currentWorkPermit, 'tenancy.tenancyContractors', [])
  const currentUser = rootGetters['user/currentUser'] || {}
  const isTenancyContractor = tenancyContractors.some(item =>
    item.userId === currentUser.id && item.companyId === getters.currentWorkPermit.contractorId)
  return isTenancyContractor || getIsEntitledTenancyAccount(getters, rootGetters)
}

export default {
  namespaced: true,
  state: defaultState,
  getters,
  actions,
  mutations,
}
